import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileInput, X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal de création d'un devis signé HORS PitBoard (Henry, Excel, papier...).
 * - Compté dans le KPI "Devis signés" du dashboard.
 * - Numéroté EXT-YYYY-NNN pour ne pas interférer avec la séquence DEV-YYYY-NNN.
 * - Statut figé à "accepte". Pas de PDF généré, pas de fiche matériel.
 *
 * Mappe sur POST /api/quotes/external (modèle ExternalQuoteIn).
 */
export default function ExternalQuoteModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    client_text: "",
    description: "",
    amount_ht: "",
    vat_rate: 20,
    signed_at: new Date().toISOString().slice(0, 10),
    external_reference: "",
  });
  const [vatFranchise, setVatFranchise] = useState(false);
  const [saving, setSaving] = useState(false);

  // Récupère le mode (franchise TVA) pour adapter la TVA par défaut
  useEffect(() => {
    let cancel = false;
    api.get("/company").then(({ data }) => {
      if (cancel) return;
      const vf = !!data?.vat_franchise;
      setVatFranchise(vf);
      if (vf) setForm((f) => ({ ...f, vat_rate: 0 }));
    }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  const submit = async () => {
    if (!form.client_text.trim()) {
      toast.error("Merci de renseigner le nom du client.");
      return;
    }
    const amt = Number(form.amount_ht);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Le montant HT doit être strictement positif.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_text: form.client_text.trim(),
        description: form.description.trim(),
        amount_ht: amt,
        vat_rate: Number(form.vat_rate) || 0,
        signed_at: form.signed_at || null,
        external_reference: form.external_reference.trim() || null,
      };
      const { data } = await api.post("/quotes/external", payload);
      toast.success(`✅ Devis externe ${data.number} créé — comptabilisé dans vos indicateurs.`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création du devis externe.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="external-quote-modal">
      <div className="modal max-w-xl">
        <header className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FileInput size={18} style={{ color: "var(--pit-yellow)" }} />
            <h3 className="font-display text-xl tracking-tight" style={{ color: "var(--chalk)" }}>
              Nouveau devis externe
            </h3>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="external-quote-close">
            <X size={14} />
          </button>
        </header>

        <p className="text-[12.5px] mb-5" style={{ color: "var(--ink-soft)" }}>
          Pour un devis <b>déjà signé</b> hors PitBoard (Henry, Excel, papier…).
          Il sera ajouté au compteur <b>« Devis signés »</b> et contribuera à la projection de trésorerie,
          mais ne génèrera pas de PDF ni de fiches matériel.
        </p>

        <div className="grid gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>
              Client *
            </label>
            <input
              type="text" className="input mt-1"
              placeholder="Ex: M. Dupont · Maison Martin SAS"
              value={form.client_text}
              onChange={(e) => setForm({ ...form, client_text: e.target.value })}
              data-testid="ext-client"
              autoFocus
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>
              Description / chantier
            </label>
            <input
              type="text" className="input mt-1"
              placeholder="Ex: Rénovation salle de bain"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="ext-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>
                Montant {vatFranchise ? "TTC" : "HT"} *
              </label>
              <input
                type="number" step="0.01" className="input mt-1 numerals"
                placeholder="Ex: 12500"
                value={form.amount_ht}
                onChange={(e) => setForm({ ...form, amount_ht: e.target.value })}
                data-testid="ext-amount"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>
                TVA
              </label>
              <select
                className="input mt-1"
                value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: Number(e.target.value) })}
                disabled={vatFranchise}
                data-testid="ext-vat"
              >
                <option value={0}>0 % (franchise TVA)</option>
                <option value={5.5}>5,5 % (réduit)</option>
                <option value={10}>10 % (intermédiaire)</option>
                <option value={20}>20 % (normal)</option>
              </select>
              {vatFranchise && (
                <div className="text-[10px] mt-1" style={{ color: "var(--grey)" }}>
                  Franchise TVA active — taux forcé à 0.
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>
                Date signature
              </label>
              <input
                type="date" className="input mt-1"
                value={form.signed_at}
                onChange={(e) => setForm({ ...form, signed_at: e.target.value })}
                data-testid="ext-signed-at"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>
                Référence externe (optionnel)
              </label>
              <input
                type="text" className="input mt-1"
                placeholder="Ex: Henry #2025-042"
                value={form.external_reference}
                onChange={(e) => setForm({ ...form, external_reference: e.target.value })}
                data-testid="ext-reference"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn btn-outline" disabled={saving}>
            Annuler
          </button>
          <button onClick={submit} className="btn btn-accent" disabled={saving} data-testid="ext-save">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Enregistrement…</>
                    : <><Save size={14} /> Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  );
}
